"use client";

import StyledGreenCheck from "@/app/assets/icons/styled-green-check";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { ChevronDown, Star, Wallet } from "lucide-react";
import brokingHeroBg2 from "@/app/assets/images/broking-hero-bg-2.webp";
import BrokingChargeBreakdown from "@/app/broking/icm 3.0/shared/brocking-charge-breakdown";

const CARD_CORNER_BG = {
    size: "55% auto",
    position: "right bottom",
} as const;

const pricingPlans = [
    {
        badge: "Pay as you go",
        badgeColor:
            "rounded-lg border border-[#FDE7D7] bg-[#FEF5EE] text-brand-primary-500",
        BadgeIcon: Wallet,
        title: "Per Trade Plan",
        subtitle: "Flat ₹9 per executed order — all segments",
        features: [
            "All segments: Equity, F&O, MCX, NCDEX",
            "Full product suite access",
            "No monthly commitment",
            "No brokerage on zero-trade months",
        ],
        price: "9",
        priceLabel: "Per order",
    },
    {
        badge: "Unlimited Trading",
        badgeColor: "bg-brand-primary-500 text-white",
        BadgeIcon: Star,
        title: "Monthly Brokerage Plan",
        subtitle: "₹0 brokerage on every trade",
        features: [
            "Unlimited trades, flat monthly cap",
            "Trade Cash, Futures & Options",
            "Full product suite access",
            "Change plan anytime via email",
        ],
        price: "999",
        priceLabel: "Per month",
    },
] as const;

export default function WhatStockoProvides() {
    return (
        <section
            aria-labelledby="pricing-heading"
            className="w-full px-5 py-10 md:px-[120px] md:pb-0 md:pt-[120px]"
        >
            <div className="mx-auto box-border flex w-full max-w-[1200px] flex-col items-center gap-10 rounded-2xl bg-[#FDE7D7] px-5 py-10 md:px-[74px] md:py-[42px]">
                <header className="flex w-full flex-col items-center gap-2 text-center">
                    <h2
                        id="pricing-heading"
                        className="text-center text-2xl font-bold leading-8 tracking-[var(--typography-font-letter-spacing-xs)] text-[var(--colors-text-header)] md:text-[length:var(--typography-font-size-11xl)] md:leading-[var(--typography-font-line-height-11xl)]"
                    >
                        Simple. Flat. No Surprises.
                    </h2>
                    <p className="text-center text-base font-regular leading-[var(--typography-font-line-height-m)] tracking-[var(--typography-font-letter-spacing-xs)] text-[var(--colors-text-sub-header)] md:text-[length:var(--typography-font-size-xl)] md:leading-[var(--typography-font-line-height-xl)]">
                        Pay only when you trade. No prior commitments, no platform charges, no
                        hidden fees. Ever.
                    </p>
                </header>

                <div className="flex w-full flex-col items-center gap-5 md:flex-row md:justify-between md:gap-6">
                    {pricingPlans.map((plan) => (
                        <article
                            key={plan.title}
                            className="relative flex h-auto w-full max-w-[469px] flex-col overflow-hidden rounded-2xl bg-white md:h-[382px] md:w-[469px] md:shrink-0 md:rounded-[20px] md:p-6"
                            style={{
                                backgroundImage: `url('${brokingHeroBg2.src}')`,
                                backgroundPosition: CARD_CORNER_BG.position,
                                backgroundSize: CARD_CORNER_BG.size,
                                backgroundRepeat: "no-repeat",
                            }}
                        >
                            <div className="relative z-10 flex flex-col gap-6 px-5 pb-24 pt-8 md:gap-8 md:p-0">
                                <div className="flex flex-col gap-3">
                                    <div
                                        className={`inline-flex w-fit items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold leading-[1.6] tracking-[0.12px] md:gap-2 md:px-s5 md:py-s3 md:text-sm md:tracking-[0.14px] ${plan.badgeColor}`}
                                    >
                                        <plan.BadgeIcon
                                            className="size-[11px] md:size-3"
                                            aria-hidden
                                        />
                                        {plan.badge}
                                    </div>

                                    <div className="flex flex-col">
                                        <h3 className="text-lg font-bold leading-8 text-[var(--colors-text-header)] md:text-[30px] md:leading-9">
                                            {plan.title}
                                        </h3>
                                        <p className="text-xs font-medium leading-[22px] text-[var(--colors-text-sub-header)] md:text-base">
                                            {plan.subtitle}
                                        </p>
                                    </div>
                                </div>

                                <ul className="flex flex-col gap-3">
                                    {plan.features.map((feature) => (
                                        <li
                                            key={feature}
                                            className="flex items-center gap-2.5 text-xs font-regular leading-[22px] text-[var(--colors-text-sub-header)] md:text-sm"
                                        >
                                            <StyledGreenCheck className="shrink-0" aria-hidden />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="absolute bottom-5 right-3 z-10 text-right leading-none md:bottom-6 md:right-6">
                                <div className="flex items-start justify-end gap-0.5">
                                    <span className="mt-1 text-[29px] font-bold leading-[1.3] text-[var(--colors-text-header)] md:text-4xl">
                                        ₹
                                    </span>
                                    <span className="text-[76px] font-bold leading-[1.1] tracking-[-2.29px] text-[var(--colors-text-header)] md:text-[74px] md:leading-none md:tracking-tight">
                                        {plan.price}
                                    </span>
                                </div>
                                <p className="mt-1 text-base font-medium leading-[22px] text-[var(--colors-text-sub-header)] md:mt-2 md:text-xl md:leading-[26px]">
                                    {plan.priceLabel}
                                </p>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="flex w-full justify-end md:mt-[22px]">
                    <Accordion
                        type="single"
                        collapsible
                        className="flex w-full flex-col items-end"
                    >
                        <AccordionItem
                            value="charge-breakdown"
                            className="flex w-full flex-col items-end !border-none"
                        >
                            <AccordionTrigger
                                showIcon={false}
                                className="ml-auto inline-flex w-fit flex-none items-center justify-center gap-s2 rounded-lg !border-none !py-0 font-[family-name:var(--typography-font-family-label)] text-[length:var(--typography-font-size-s)] font-medium leading-[var(--typography-font-line-height-s)] tracking-[var(--typography-font-letter-spacing-xs)] text-[var(--colors-text-link-text)] underline decoration-solid decoration-[12%] underline-offset-[25%] hover:underline [&[data-state=open]>svg]:rotate-180"
                            >
                                View Detailed Charge Breakdown
                                <ChevronDown
                                    size={16}
                                    className="shrink-0 transition-transform duration-200"
                                />
                            </AccordionTrigger>
                            <AccordionContent className="w-full pt-3 md:pt-4">
                                <BrokingChargeBreakdown />
                                
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </section>
    );
}
